// Keep slash at the end to force userland punycode module instead of deprecated node builtin
import punycode from 'punycode/';

interface Options {
  usePunycode?: boolean;
  forDisplay?: boolean;
}

export function normalizeWebsitePattern(websitePatternString: string, options: Options = {}) {
  const { hostname, port, pathname, search, hash } = getNormalizedPatternParts(
    websitePatternString.trim(),
    options,
  );

  const basePattern = `${hostname}${port ? ':' : ''}${port}${pathname}`;
  if (options.forDisplay) {
    return `${basePattern}${search ? '?…' : ''}${hash ? '#…' : ''}`;
  }

  return `${basePattern}${search}${hash}`;
}

export function getNormalizedPatternParts(
  inputWebsitePatternString: string,
  { usePunycode = false }: Options = {},
): { hostname: string; pathname: string; port: string; hash: string; search: string } {
  // Don't treat numerical inputs as urls. These will be treated as IPv4 addresses, which makes no
  // sense to a normal user
  if (/^\d+$/.test(inputWebsitePatternString)) {
    return {
      hostname: inputWebsitePatternString,
      pathname: '',
      port: '',
      hash: '',
      search: '',
    };
  }

  // Don't handle any string starting with "\\". These are windows UNC paths, which we can't handle
  // in any way down the line. People still want to put their UNC related logins into heylogin and
  // fill them in manually.
  if (/^\\\\/.test(inputWebsitePatternString)) {
    return {
      hostname: inputWebsitePatternString,
      pathname: '',
      port: '',
      hash: '',
      search: '',
    };
  }

  try {
    let websitePatternString = inputWebsitePatternString;

    // Normal users will put in urls without protocol expecting them to be treated correctly, so we
    // add a dummy just for parsing. It is always dropped again later on. Note that we need to add a
    // real protocol like "https" here, since browsers treat custom protocols inconsistently.
    const hasProtocol = /^([a-z]+:)?\/\//.exec(websitePatternString);
    if (!hasProtocol) {
      websitePatternString = `https://${websitePatternString}`;
    }

    const url = new URL(websitePatternString);

    const { port, hash, search } = url;
    const hostname = normalizePatternHostname(url.hostname, usePunycode);
    const pathname = normalizePatternPathname(url, !!hash || !!search);

    return {
      hostname,
      pathname,
      port,
      hash,
      search,
    };
  } catch {
    // If we can't parse the url, just return it. Trying to be clever here very easily makes the
    // function not be stable (that is applying it on its own output will not give that same
    // output), which would be weird.
    return {
      hostname: inputWebsitePatternString,
      pathname: '',
      port: '',
      hash: '',
      search: '',
    };
  }
}

function normalizePatternHostname(hostname: string, usePunycode: boolean) {
  // We always get a punycoded hostname (since it previously passed through the URL constructor). If
  // the caller doesn't want that, convert back to unicode.
  const hostnamePunycodedIfNeeded = usePunycode ? hostname : punycode.toUnicode(hostname);
  return hostnamePunycodedIfNeeded.toLowerCase();
}

function normalizePatternPathname(url: URL, hasSearchOrHash: boolean) {
  const pathname = url.pathname.trim();
  if (pathname.match(/^\/+$/)) {
    return hasSearchOrHash ? '/' : '';
  }
  return pathname;
}
